import { useEffect, useState } from "react";
import { Check, Users, Droplet } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";

function ChallengeCard({ c, onDone }) {
  const [open, setOpen] = useState(false);
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);

  const complete = async () => {
    setSaving(true);
    try {
      await portalApi.post(`/portal/challenges/${c.id}/complete`, { reflection });
      onDone();
      setOpen(false);
    } catch (e) { alert(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid={`challenge-${c.id}`} className={`border rounded-xl p-6 transition-colors ${c.completed ? "bg-[#EAE3D9]/50 border-[#D8CEBE]" : "bg-white/60 border-[#E0D8C9]"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          {c.scheduled_for && <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-1">{new Date(c.scheduled_for).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>}
          <h3 className="font-serif-display text-2xl">{c.title}</h3>
          {c.description && <p className="text-[#5E4B3C] font-body mt-2 leading-relaxed">{c.description}</p>}
          <p className="text-xs text-[#8a7a6a] font-body mt-3 flex items-center gap-3">
            <span className="text-[#A9724E] flex items-center gap-1"><Droplet className="w-3.5 h-3.5" strokeWidth={1.5} />{c.points} drops</span>
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" strokeWidth={1.5} />{c.completed_count} completed</span>
          </p>
        </div>
        {c.completed ? (
          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#A9724E] text-[#F4EFE6] px-4 py-2 text-xs tracking-wide"><Check className="w-4 h-4" strokeWidth={2} />Done</span>
        ) : (
          <button data-testid={`complete-${c.id}`} onClick={() => setOpen((o) => !o)} className="shrink-0 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-5 py-2 text-xs tracking-wide transition-colors">Mark complete</button>
        )}
      </div>
      {open && !c.completed && (
        <div className="mt-5 border-t border-[#E0D8C9] pt-5">
          <label className="block text-xs tracking-overline uppercase text-[#8a7a6a] mb-2">A reflection (optional)</label>
          <textarea data-testid={`reflection-${c.id}`} value={reflection} onChange={(e) => setReflection(e.target.value)} rows={3}
            className="w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E]" placeholder="What surfaced for you?" />
          <button onClick={complete} disabled={saving} data-testid={`submit-complete-${c.id}`} className="mt-3 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-xs tracking-wide transition-colors disabled:opacity-50">{saving ? "Saving…" : "Complete challenge"}</button>
        </div>
      )}
    </div>
  );
}

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => portalApi.get("/portal/challenges").then((r) => setChallenges(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="portal-challenges">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">The practice</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-10">Challenges</h1>
      {loading ? <p className="text-[#8a7a6a] font-body">Loading…</p> : challenges.length === 0 ? (
        <p className="text-[#8a7a6a] font-body">No challenges posted yet. Your first one is coming soon.</p>
      ) : (
        <div className="space-y-4 max-w-3xl">
          {challenges.map((c) => <ChallengeCard key={c.id} c={c} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
